import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CurrencyPipe, DatePipe, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PortalPedidoService } from '../../shared/services/apis/compras/portal-pedido.service';
import { PortalPedidoStatusEnum } from '../../shared/enums/compras/portal-pedido-status.enum';
import { PortalPedidoDetalheResponse } from '../../shared/services/responses/compras/portal-pedido-detalhe.response';
import { PortalPedidoResumoResponse } from '../../shared/services/responses/compras/portal-pedido-resumo.response';

type PortalState = 'loading' | 'invalid' | 'gate' | 'view' | 'encerrado' | 'sucesso';

// Página PÚBLICA (fora do shell autenticado) onde o fornecedor visualiza e
// confirma o pedido de compra. Credencial = token do link; barreira secundária =
// 5 primeiros dígitos do documento. Conteúdo somente leitura (pedido fechado).
@Component({
  selector: 'app-portal-pedido',
  standalone: true,
  imports: [FormsModule, CurrencyPipe, DatePipe],
  templateUrl: './portal-pedido.component.html',
  styleUrls: [
    '../../shared/styles/portal-preview-shared.css',
    '../cotacao/portal-cotacao.component.css',
    './portal-pedido.component.css',
  ],
})
export class PortalPedidoComponent implements OnInit {
  public state: PortalState = 'loading';
  public token = '';
  public documento = '';
  public gateErro = '';
  public abrindo = false;
  public confirmando = false;

  public resumo: PortalPedidoResumoResponse | null = null;
  public detalhe: PortalPedidoDetalheResponse | null = null;

  public readonly StatusEnum = PortalPedidoStatusEnum;

  private readonly platformId = inject(PLATFORM_ID);

  constructor(
    private route: ActivatedRoute,
    private portalService: PortalPedidoService
  ) {}

  public ngOnInit(): void {
    // No SSR renderizamos apenas o "loading": a chamada HTTP anônima falharia no
    // servidor e assaria o estado "invalid" no HTML, causando um flash de
    // "Link inválido" antes da hidratação. A busca real acontece só no browser.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.token = this.route.snapshot.paramMap.get('token') ?? '';
    if (!this.token) {
      this.state = 'invalid';
      return;
    }

    this.portalService.resumo(this.token).subscribe({
      next: res => {
        this.resumo = res;
        if (res.isError || res.status === PortalPedidoStatusEnum.Invalido) {
          this.state = 'invalid';
          return;
        }
        if (res.status === PortalPedidoStatusEnum.Encerrado) {
          this.state = 'encerrado';
          return;
        }
        // Pedido confirmado continua visualizável (somente leitura) — passa pelo
        // mesmo gate quando há documento cadastrado.
        if (res.requerDocumento) {
          this.state = 'gate';
        } else {
          this.abrir();
        }
      },
      error: () => (this.state = 'invalid'),
    });
  }

  public get podeAbrir(): boolean {
    return /^\d{5}$/.test(this.documento.trim());
  }

  public abrir(): void {
    if (this.abrindo) return;

    this.gateErro = '';
    if (this.resumo?.requerDocumento && !this.podeAbrir) {
      this.gateErro = 'Informe os 5 primeiros dígitos do documento.';
      return;
    }

    this.abrindo = true;
    this.portalService.abrir(this.token, this.documento).subscribe({
      next: res => {
        this.abrindo = false;
        if (res.isError) {
          this.gateErro = res.errorDescription || 'Não foi possível abrir o pedido.';
          this.state = this.resumo?.requerDocumento ? 'gate' : 'invalid';
          return;
        }
        this.detalhe = res;
        this.state = res.status === PortalPedidoStatusEnum.Encerrado ? 'encerrado' : 'view';
      },
      error: () => {
        this.abrindo = false;
        this.gateErro = 'Não foi possível abrir o pedido.';
      },
    });
  }

  public confirmar(): void {
    if (!this.detalhe || this.confirmando || this.isConfirmado) return;

    this.confirmando = true;
    this.gateErro = '';
    this.portalService.confirmar(this.token, this.documento).subscribe({
      next: res => {
        this.confirmando = false;
        if (res.isError) {
          this.gateErro = res.errorDescription || 'Não foi possível confirmar o pedido.';
          return;
        }
        this.detalhe = res;
        this.state = 'sucesso';
      },
      error: () => {
        this.confirmando = false;
        this.gateErro = 'Não foi possível confirmar o pedido.';
      },
    });
  }

  public get isConfirmado(): boolean {
    return this.detalhe?.status === PortalPedidoStatusEnum.Confirmado;
  }

  public isAtivo(chave: string): boolean {
    // Sem configuração de comunicação-pedido salva, camposAtivos vem vazio — mostra
    // todas as seções (mesma semântica dos documentos planilha/PDF).
    const campos = this.detalhe?.camposAtivos ?? [];
    return campos.length === 0 || campos.includes(chave);
  }

  public get heroGradient(): string {
    const d = this.detalhe ?? this.resumo;
    return `linear-gradient(135deg, ${d?.corHeroInicio ?? '#1C1B1F'}, ${d?.corHeroFim ?? '#2D2A3A'})`;
  }

  public get corMarca(): string {
    return (this.detalhe ?? this.resumo)?.corMarca ?? '#B73232';
  }
}
